import { HttpClient, HttpHeaders, HttpParams, httpResource } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { REQUIRE_AUTH_HEADER } from '@psu-pennstateit-swe/utils/security';
import { map, Observable, of } from 'rxjs';
import { SSRM_CONFIG } from '../../app-properties.model';
import { Role, rolesResponseSchema, ServerRole, serverRoleSchema, toClientRole } from './roles.model';

const headers = new HttpHeaders({ [REQUIRE_AUTH_HEADER]: 'true' });
const roleLookupSchema = serverRoleSchema.pick({ name: true, externalId: true }).array();

@Injectable({
  providedIn: 'root',
})
export class RolesData {
  private readonly http = inject(HttpClient);
  private readonly ssrmConfig = inject(SSRM_CONFIG);
  private readonly baseUrl = `${this.ssrmConfig.url.replace(/\/+$/, '')}/roles`;

  readonly rolesResource = httpResource<Role[]>(
    () => ({
      url: this.baseUrl,
      method: 'GET',
      headers,
    }),
    {
      parse: (response) => rolesResponseSchema.parse(response),
    },
  );

  createRole(name: string, externalId: string): Observable<Role> {
    return this.http
      .post<ServerRole>(this.baseUrl, { name, externalId }, { headers })
      .pipe(map((role) => toClientRole(serverRoleSchema.parse(role))));
  }

  checkRoleExists(query: { name?: string; externalId?: string }): Observable<boolean> {
    let params = new HttpParams();
    if (query.name !== undefined) {
      params = params.set('name', query.name);
    }
    if (query.externalId !== undefined) {
      params = params.set('externalId', query.externalId);
    }
    if (!params.keys().length) {
      return of(false);
    }

    return this.http
      .get<ServerRole[]>(this.baseUrl, { headers, params })
      .pipe(
        map((response) =>
          roleLookupSchema
            .parse(response)
            .some(
              (role) =>
                (query.name === undefined || role.name === query.name) &&
                (query.externalId === undefined || role.externalId === query.externalId),
            ),
        ),
      );
  }
}
